'use client';

import { Close as CancelIcon } from '@mui/icons-material';
import {
	Button,
	CircularProgress,
	FormControlLabel,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Switch,
	TextField,
} from '@mui/material';
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { InverseButton } from '@parkchirp/ui-library';
import React, { Dispatch, FC, SetStateAction, useState } from 'react';

import { PromoCode, RateV2 } from '../../../types';
import { Dayjs, dayjs } from '../../../utils';
import { IntegrationSelect } from './integrationSelect';

interface Props {
	rateName: string;
	setRateName: Dispatch<SetStateAction<string>>;
	passType: string;
	setPassType: Dispatch<SetStateAction<string>>;
	location: string;
	setLocation: Dispatch<SetStateAction<string>>;
	accessHours: string;
	setAccessHours: Dispatch<SetStateAction<string>>;
	status: number;
	online: string;
	onlineStart: Dayjs;
	onlineEnd: Dayjs;
	setOnlineStart: Dispatch<SetStateAction<Dayjs>>;
	setOnlineEnd: Dispatch<SetStateAction<Dayjs>>;
	setOnline: Dispatch<SetStateAction<string>>;
	description: string;
	setDescription: Dispatch<SetStateAction<string>>;
	makeModelPlate: string;
	setMakeModelPlate: Dispatch<SetStateAction<string>>;
	setTabValue: Dispatch<SetStateAction<string>>;
	missingFields: string;
	setMissingFields: Dispatch<SetStateAction<string>>;
	promoCodes: PromoCode[];
	setPromoCodes: Dispatch<SetStateAction<PromoCode[]>>;
	selectedRate: RateV2;
	setActiveStep: Dispatch<SetStateAction<number>>;
	loadingFees: boolean;
	isChild: boolean;
	integrations: string[];
	setIntegrations: Dispatch<SetStateAction<string[]>>;
	minOnlineStartDate: Dayjs;
	facilityOnlineStartDate?: string;
	facilityOnlineEndDate?: Dayjs | null;
	timezone: string;
	theme: any;
	isDefault: boolean;
	facilityIntegration: string;
	soldOut: boolean;
	setSoldOut: Dispatch<SetStateAction<boolean>>;
}

const General: FC<Props> = ({
	rateName,
	setRateName,
	passType,
	setPassType,
	location,
	setLocation,
	accessHours,
	setAccessHours,
	status,
	online,
	setOnline,
	description,
	setDescription,
	makeModelPlate,
	setMakeModelPlate,
	setTabValue,
	missingFields,
	setMissingFields,
	promoCodes,
	setPromoCodes,
	onlineStart,
	onlineEnd,
	setOnlineStart,
	setOnlineEnd,
	selectedRate,
	setActiveStep,
	loadingFees,
	isChild,
	integrations,
	setIntegrations,
	minOnlineStartDate,
	facilityOnlineStartDate,
	facilityOnlineEndDate,
	timezone,
	theme,
	isDefault,
	facilityIntegration,
	soldOut,
	setSoldOut,
}) => {
	const [onlyShowActiveRates, setOnlyShowActiveRates] = useState<boolean>(false);

	const handleNext = () => {
		const now = dayjs().tz(timezone, true);
		const requiredFields = {
			rateName,
			passType,
			accessHours,
			status,
			online,
			makeModelPlate,
			promoCodes,
			onlineStart,
			...(facilityOnlineEndDate && !isDefault && { onlineEnd }),
		};
		let errorMessage = '';
		if (online === 'PROMO_CODE_REQUIRED') {
			if (!promoCodes.length) {
				errorMessage += 'promoCodes, expiresOn, allowedUses,';
				setPromoCodes((prevState) => [...prevState, { code: '', allowedUses: 0, expiresOn: '' }]);
			} else {
				promoCodes.forEach((c) => {
					if (!c.code) {
						errorMessage += 'promoCode,';
					}
					if (c.allowedUses <= 0) {
						errorMessage += 'allowedUses,';
					}
					if (!c.expiresOn || dayjs(c.expiresOn, timezone).isAfter(dayjs.tz(onlineEnd, timezone))) {
						errorMessage += 'expiresOn,';
					}
				});
			}
		}
		if (online === 'ADMIN_SIGN_UP_ONLY') {
			delete requiredFields.makeModelPlate;
			delete requiredFields.onlineStart;
			delete requiredFields.promoCodes;
			delete requiredFields.onlineEnd;
		} else {
			if (facilityIntegration === 'GET_MY_PARKING' && makeModelPlate !== 'REQUIRED') {
				errorMessage += 'gmpError,';
			}
			let onlineStartHasBeenChangedFromSelectedRatesOnlineStartDate = false;
			if (selectedRate?.onlineStartDate && onlineStart) {
				onlineStartHasBeenChangedFromSelectedRatesOnlineStartDate = !dayjs(selectedRate.onlineStartDate)
					.tz(timezone)
					.isSame(onlineStart);
			} else if (!selectedRate?.onlineStartDate && onlineStart) {
				onlineStartHasBeenChangedFromSelectedRatesOnlineStartDate = true;
			}
			if (
				!onlineStart?.isValid() ||
				(onlineStartHasBeenChangedFromSelectedRatesOnlineStartDate &&
					(onlineStart.isBefore(minOnlineStartDate, 'minute') || onlineStart.isBefore(now, 'day')))
			) {
				errorMessage += 'onlineStart,';
			}
			if (
				onlineStartHasBeenChangedFromSelectedRatesOnlineStartDate &&
				facilityOnlineStartDate &&
				onlineStart &&
				onlineStart.isBefore(facilityOnlineStartDate)
			) {
				errorMessage += 'onlineStartBeforeFacilityOnlineStartDate,';
			}

			if (
				onlineStartHasBeenChangedFromSelectedRatesOnlineStartDate &&
				onlineStart &&
				onlineStart.isBefore(now, 'day')
			) {
				errorMessage += 'onlineStartBeforeNow,';
			}

			if (onlineEnd !== null) {
				if (!dayjs(onlineEnd).isValid()) {
					errorMessage += 'onlineEnd,';
				}
				if (onlineEnd.isAfter(facilityOnlineEndDate, 'minute')) {
					errorMessage += 'onlineEndAfterFacilityOnlineEndDate,';
				}
				if (onlineStart.isAfter(onlineEnd, 'minute')) {
					errorMessage += 'dateComp,';
				}
			}
		}
		if (!selectedRate) {
			delete requiredFields.status;
		}
		Object.entries(requiredFields).forEach((e) => {
			const key = e[0];
			const value = e[1];
			if (!value && value !== 0) {
				const newValue = `${key}, `;
				if (newValue === 'online, ') {
					errorMessage += 'onlineOnly,';
				} else {
					errorMessage += newValue;
				}
			}
		});
		if (errorMessage) {
			setMissingFields(errorMessage);
		} else {
			setMissingFields('');
			setTabValue('3');
			setActiveStep(2);
		}
	};

	const handlePromoCodeInput = (value, index, param) => {
		promoCodes[index][param] = value;
		setPromoCodes([...promoCodes]);
	};

	const handleDeletePromoCode = (index) => {
		promoCodes.splice(index, 1);
		setPromoCodes([...promoCodes]);
	};

	const showPromoCodes = promoCodes && online === 'PROMO_CODE_REQUIRED';
	function getDisplayValueForExpiredPromoCode(promoCode: PromoCode): string | null {
		// We hide a promo code if the expiresOn field is before the current day.
		// A new promo code has an empty expiresOn field, so we skip the logic
		// of hiding that promo code, so the user can make changes to it.
		if (onlyShowActiveRates && promoCode.expiresOn) {
			const beginningOfToday = dayjs().utcOffset(0).startOf('date').toDate();
			return new Date(promoCode.expiresOn) >= beginningOfToday ? null : 'none';
		}
		return null;
	}

	return (
		<Grid spacing={2} container>
			<Grid item xs={6}>
				<InputLabel>
					Rate Name <span className='pca__ratesForm-required'>*</span>
				</InputLabel>
				<TextField
					required
					fullWidth
					value={rateName}
					error={missingFields.includes('rateName')}
					onChange={(e) => setRateName(e.target.value)}
					inputProps={{ 'data-cy': 'rate-name-input' }}
				/>
			</Grid>
			<Grid item xs={6}>
				<InputLabel>
					Pass Type <span className='pca__ratesForm-required'>*</span>
				</InputLabel>
				<Select
					fullWidth
					value={passType}
					required
					disabled={isChild}
					error={missingFields.includes('passType')}
					onChange={(e) => setPassType(e.target.value)}
					data-cy='pass-type-select-menu'
				>
					<MenuItem value='RESERVED'>Reserved</MenuItem>
					<MenuItem value='UNRESERVED'>Unreserved</MenuItem>
				</Select>
			</Grid>
			<Grid item xs={6}>
				<InputLabel>
					Access Hours <span className='pca__ratesForm-required'>*</span>
				</InputLabel>
				<Select
					fullWidth
					value={accessHours}
					required
					disabled={isChild}
					error={missingFields.includes('accessHours')}
					onChange={(e) => setAccessHours(e.target.value)}
					data-cy='access-hours-select-menu'
				>
					<MenuItem value='TWENTY_FOUR_SEVEN_ACCESS'>24/7 Access</MenuItem>
					<MenuItem value='NIGHTS_AND_WEEKENDS'>Nights and Weekends</MenuItem>
					<MenuItem value='M_F_WEEKDAY_DAYTIME'>M-F Weekday Daytime</MenuItem>
				</Select>
			</Grid>
			<Grid item xs={6}>
				<InputLabel>Location</InputLabel>
				<TextField
					fullWidth
					value={location}
					disabled={isChild}
					onChange={(e) => setLocation(e.target.value)}
					inputProps={{ 'data-cy': 'location-input' }}
				/>
			</Grid>
			<Grid item xs={6}>
				<InputLabel>
					Display Online<span className='pca__ratesForm-required'>*</span>
				</InputLabel>
				<Select
					fullWidth
					value={online}
					required
					disabled={isDefault}
					onChange={(e) => {
						if (e.target.value === 'ADMIN_SIGN_UP_ONLY') {
							setOnlineStart(null);
							setOnlineEnd(null);
						}
						setOnline(e.target.value);
						setOnlineStart(
							facilityOnlineStartDate && dayjs().isBefore(facilityOnlineStartDate, 'minute')
								? dayjs(facilityOnlineStartDate).startOf('day')
								: dayjs().tz(timezone, true),
						);
						if (!onlineEnd) {
							if (selectedRate?.onlineEndDate) {
								setOnlineEnd(dayjs(selectedRate.onlineEndDate).tz(timezone));
							} else if (facilityOnlineEndDate) {
								setOnlineEnd(dayjs(facilityOnlineEndDate).tz(timezone));
							}
						}
					}}
					error={missingFields.includes('onlineOnly')}
					data-cy='display-online-select-menu'
				>
					<MenuItem value='YES'>Yes</MenuItem>
					<MenuItem value='PROMO_CODE_REQUIRED'>Promo Code Required</MenuItem>
					<MenuItem value='ADMIN_SIGN_UP_ONLY'>Admin Sign Up Only</MenuItem>
				</Select>
			</Grid>
			<Grid item xs={6}>
				<InputLabel>Availability</InputLabel>
				<FormControlLabel
					control={<Switch checked={soldOut} onChange={(e) => setSoldOut(e.target.checked)} color='error' />}
					label={soldOut ? 'Sold Out' : 'Available'}
				/>
			</Grid>
			{!isDefault && (
				<Grid item xs={16}>
					<IntegrationSelect integrations={integrations} setIntegrations={setIntegrations} />
				</Grid>
			)}
			{online && online !== 'ADMIN_SIGN_UP_ONLY' && (
				<>
					<Grid item xs={6}>
						{missingFields.includes('dateComp') && (
							<p className='pca__ratesForm-required'>Start date must be before end date</p>
						)}
						{missingFields.includes('onlineStartBeforeFacilityOnlineStartDate') && (
							<p className='pca__ratesForm-required'>
								Start date cannot be before the facility&apos;s online sales start date
							</p>
						)}
						{missingFields.includes('onlineStartBeforeNow') && (
							<p className='pca__ratesForm-required'>Start date cannot be before now/today</p>
						)}
						<InputLabel>
							Online Sales Start Date
							<span className='pca__ratesForm-required'>*</span>
						</InputLabel>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DateTimePicker
								format='MM/DD/YYYY hh:mm a'
								value={onlineStart}
								onChange={(newValue) => {
									if (newValue) {
										setOnlineStart(dayjs(newValue).tz(timezone, true));
									} else {
										setOnlineStart(null);
									}
								}}
								slotProps={{
									textField: { error: missingFields.includes('onlineStart'), fullWidth: true },
								}}
								minDate={minOnlineStartDate}
								maxDate={facilityOnlineEndDate || null}
							/>
						</LocalizationProvider>
					</Grid>
					<Grid item xs={6}>
						{missingFields.includes('dateComp') && (
							<p className='pca__ratesForm-required' style={{ visibility: 'hidden' }}>
								Start date must be before end date
							</p>
						)}
						{missingFields.includes('onlineEndAfterFacilityOnlineEndDate') && (
							<p className='pca__ratesForm-required'>
								End date cannot be after the facility&apos;s online sales end date
							</p>
						)}
						<InputLabel>
							Online Sales End Date
							{facilityOnlineEndDate && <span className='pca__ratesForm-required'>*</span>}
						</InputLabel>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DateTimePicker
								format='MM/DD/YYYY hh:mm a'
								value={onlineEnd}
								onChange={(newValue) => {
									if (newValue) {
										setOnlineEnd(dayjs(newValue).tz(timezone));
									} else {
										setOnlineEnd(null);
									}
								}}
								slotProps={{
									textField: { error: missingFields.includes('onlineEnd'), fullWidth: true },
								}}
								minDate={dayjs()}
								maxDate={facilityOnlineEndDate || null}
							/>
						</LocalizationProvider>
					</Grid>
					<Grid item xs={6}>
						<InputLabel>Description</InputLabel>
						<TextField fullWidth value={description} onChange={(e) => setDescription(e.target.value)} />
					</Grid>
					<Grid item xs={6}>
						<InputLabel>
							Vehicle Information
							<span className='pca__ratesForm-required'>*</span>
						</InputLabel>
						{missingFields.includes('gmpError') && (
							<p className='pca__ratesForm-required'>
								Facilities with Get My Parking must have this set to required
							</p>
						)}
						<Select
							fullWidth
							value={makeModelPlate}
							required
							onChange={(e) => setMakeModelPlate(e.target.value)}
							error={missingFields.includes('makeModelPlate')}
							data-cy='make-model-license-plate-select-menu'
						>
							<MenuItem value='REQUIRED'>Required</MenuItem>
							<MenuItem value='DO_NOT_DISPLAY'>Do Not Display</MenuItem>
							<MenuItem value='OPTIONAL'>Optional</MenuItem>
						</Select>
					</Grid>
				</>
			)}

			{showPromoCodes && promoCodes.length > 0 && (
				<div className='facility-modal-section-header'>
					<FormControlLabel
						control={
							<Switch
								disabled={!promoCodes.length}
								checked={onlyShowActiveRates}
								onChange={() => setOnlyShowActiveRates(!onlyShowActiveRates)}
							/>
						}
						label='Only show non-expired promo codes'
					/>
				</div>
			)}
			{showPromoCodes && (
				<Grid item xs={12}>
					{promoCodes.map((c, index) => (
						<Grid spacing={2} container sx={{ display: getDisplayValueForExpiredPromoCode(c) }} key={index}>
							<Grid item xs={4}>
								<InputLabel>
									Code
									<span className='pca__ratesForm-required'>*</span>
								</InputLabel>
								<TextField
									onChange={(e) => handlePromoCodeInput(e.target.value, index, 'code')}
									fullWidth
									error={missingFields.includes('promoCode')}
									value={c.code}
									inputProps={{ 'data-cy': 'promo-code-input' }}
								/>
							</Grid>
							<Grid item xs={4}>
								<InputLabel>Allowed Uses</InputLabel>
								<TextField
									onChange={(e) => {
										handlePromoCodeInput(Number(e.target.value), index, 'allowedUses');
									}}
									fullWidth
									value={c.allowedUses}
									error={missingFields.includes('allowedUses')}
								/>
							</Grid>
							<Grid item xs={3}>
								<InputLabel>
									Expires On <span className='pca__ratesForm-required'>*</span>
								</InputLabel>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DatePicker
										format='MM/DD/YYYY'
										value={dayjs(c.expiresOn).isValid() ? dayjs(c.expiresOn).tz(timezone) : null}
										onChange={(newValue) =>
											handlePromoCodeInput(
												dayjs.tz(newValue, timezone).startOf('day'),
												index,
												'expiresOn',
											)
										}
										slotProps={{ textField: { error: missingFields.includes('expiresOn') } }}
										minDate={dayjs.utc()}
										maxDate={(onlineEnd && dayjs.tz(onlineEnd, timezone)) || null}
									/>
								</LocalizationProvider>
							</Grid>
							<Grid className='pca__ratesForm-centeredGridItem' item xs={1}>
								<div style={{ visibility: 'hidden' }}>Remove</div>
								<CancelIcon
									onClick={() => handleDeletePromoCode(index)}
									className='pca__ratesForm-icon'
									fontSize='large'
									data-cy={`promo-code-delete-icon-button-${index}`}
								/>
							</Grid>
						</Grid>
					))}
					<Button
						onClick={() => {
							setPromoCodes((prevState) => [...prevState, { code: '', allowedUses: 0, expiresOn: '' }]);
						}}
						style={{ marginTop: '10px' }}
						data-cy='add-promo-code-button'
					>
						Add Promo Code
					</Button>
				</Grid>
			)}

			<Grid className='pca__ratesForm-containerPadding' spacing={2} container>
				<Grid item xs={6}>
					<InverseButton
						fullWidth
						onClick={() => {
							setTabValue('1');
							setActiveStep(0);
							if (minOnlineStartDate?.isValid() && missingFields.includes('onlineStart')) {
								// reset upon going to previous to make it easy for user to reset back to what it was
								setOnlineStart(minOnlineStartDate);
							}
						}}
						disabled={selectedRate && isDefault}
						data-cy='previous-button'
						name='Previous'
						theme={theme}
					/>
				</Grid>
				<Grid item xs={6}>
					<Button disabled={loadingFees} fullWidth onClick={handleNext} data-cy='next-step-button'>
						Next
						{loadingFees ? <CircularProgress color='inherit' size={20} /> : null}
					</Button>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default General;
